import * as React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import ImportIcon from '@mui/icons-material/ImportExport';
import { visuallyHidden } from '@mui/utils';
import { Button } from '@mui/material';
import { api, temp, ui, state } from '../../../../../interfaces/interfaces';
import AddressUtils from "@utils/address";
import ReplaySharpIcon from '@mui/icons-material/ReplaySharp';
import PreviewSharpIcon from '@mui/icons-material/PreviewSharp';
import { cacheUtils } from '../../../../..';
import { CacheTypes } from "@utils/cache/interfaces";
import AccountTypeIcon from './AccountTypeIcon';
import { logger } from "@services/workers/helpers";

const name = "JobsTable"

export interface Data {
    jobId: number,
    status: string,
    jobDescription: string,
    accountType: string,
    customer: string,
    address: string,
    postcode: string,
    dateRequired: string,
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
}

const headCells: readonly HeadCell[] = [
    {
        id: 'jobId',
        numeric: true,
        disablePadding: false,
        label: 'Job',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'jobDescription',
        numeric: false,
        disablePadding: false,
        label: 'Job Type',
    },
    {
        id: 'accountType',
        numeric: false,
        disablePadding: false,
        label: 'Account Type',
    },
    {
        id: 'customer',
        numeric: false,
        disablePadding: false,
        label: 'Customer Name',
    },
    {
        id: 'address',
        numeric: false,
        disablePadding: false,
        label: 'Address',
    },
    {
        id: 'postcode',
        numeric: false,
        disablePadding: false,
        label: 'Postcode',
    },
    {
        id: 'dateRequired',
        numeric: false,
        disablePadding: false,
        label: 'Date Required',
    },
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } =
        props;
    const createSortHandler =
        (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={'right'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface EnhancedTableToolbarProps {
    selected: readonly string[];
    numLength: number;
    validJobType: boolean;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const { selected, numLength, validJobType } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(selected.length > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {selected.length > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {selected.length} selected Job ID: {selected[0]}
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Jobs {numLength ? `(found ${numLength})` : ''}
                </Typography>
            )}
            {selected.length > 0 && <>
                <Tooltip title={`View ${selected[0]}`}>
                    <Button sx={{ whiteSpace: "nowrap", minWidth: "unset", mr: 2, }} variant='contained' color='primary' onClick={() => { state.CompletedJob = Number(selected[0]); ui.JobHistoryModal = true }} endIcon={<PreviewSharpIcon />}>
                        View Details
                    </Button>
                </Tooltip>
                <Tooltip title={`Import Address & Contact ${selected[0]}`}>
                    <Button sx={{ whiteSpace: "nowrap", minWidth: "unset" }} variant='contained' color='primary' onClick={() => importAddressAndContact(Number(selected[0]))} endIcon={<ReplaySharpIcon />}>
                        Import Address & Contact
                    </Button>
                </Tooltip>
                <Tooltip title={`Import Job ${selected[0]}`}>
                    <span>
                        <Button sx={{ whiteSpace: "nowrap", ml: 2, minWidth: "unset" }} variant='contained' color='primary' onClick={() => importJob(Number(selected[0]))} endIcon={<ImportIcon />} disabled={!validJobType}>
                            Go Back
                        </Button>
                    </span>
                </Tooltip>
            </>
            }
        </Toolbar >
    );
};

export const importJob = async (jobId: number) => {
    logger.debug(name + "->" + importJob.name, `Import will be executed of JobId: ${jobId}`)
    if (ui.ActiveStep !== 0) {
        ui.ProblemMsg = "Go Back can be only imported on first page!"
        ui.ProblemMsgVariant = "warning"
        ui.ProblemShowMsg = true
        return
    }
    const booking = await api.fetchJobByJobId(jobId)
    if (booking) {
        const completion = await api.fetchCompletedJobsByJobId(jobId)
        if (completion) {
            ui.JobSearchModal = false;
            ui.TimeFrame = {
                "value": 2160,
                "label": "3 Months"
            }
            ui.PropertyContactTitle = booking!.customerTitle.trim()
            ui.PropertyContactMobile = booking!.telephoneMobile.trim()
            ui.PropertyContactLandline = booking!.telephoneLandline.trim()
            ui.PropertyContactTitle = booking!.customerTitle.trim()
            ui.PropertyContactFirstName = booking!.customerForename.trim()
            ui.PropertyContactLastName = booking!.customerSurname.trim()
            ui.PropertyAddressBusinessName = booking!.organisation.trim()
            ui.PropertyAddressIsBusiness = booking!.organisation ? true : false
            ui.PropertyContactEmail = booking!.emailAddress.trim()
            ui.ProblemInputPostcode = booking!.postcode.trim()
            ui.AddressToMatch = `${booking!.property} ${booking!.street}${booking!.locality ? (" " + booking!.locality + ",") : ""} ${booking!.town}, ${booking!.county ? (" " + booking!.county + ",") : ""} ${booking!.postcode}`
            ui.PropertyAddressCity = booking!.town.trim()
            ui.PropertyAddressLocality = booking!.locality.trim()
            const jobType = cacheUtils.getCache<CacheTypes.JobTypeOptions>("JobTypesOptions").find((x) => x.jobTypeCode === booking!.jobTypeCode)
            jobType && (ui.ProblemComboJobSearch = jobType)
            ui.ProblemMsg = "Imported job and property details"
            ui.ProblemMsgVariant = "success"
            ui.ProblemShowMsg = true
            // job.goBackJobId = jobId
            state.JobGoBackJobId = jobId
            // job.paymentMethodId = booking.paymentMethodId
            state.JobPaymentMethodId = booking.paymentMethodId || 0
            // job.boilerAge = booking.boilerAge
            state.JobBoilerAge = booking.boilerAge || 0
            // job.paymentTaken = booking.paymentTaken
            state.JobPaymentTaken = !!booking.paymentTaken
            // job.paymentType = booking.paymentType
            state.JobPaymentType = booking.paymentType
            const jobDetails = (await api.jobDetails({ JobId: jobId }))?.[0];
            if (jobDetails && completion && completion.completionTime) {
                const completionDate = new Date(completion!.completionTime);
                const jobNote = jobDetails.notes + "\n****** GoBack Job " + jobId + " ******\nCompleted on: " + completionDate.toLocaleDateString("en-GB") + " " + completionDate.toLocaleTimeString("en-GB") + ": " + completion!.remarks;
                state.NotesPrevious = jobNote;
            }
            state.IsAGoBack = { state: true, previousJobId: jobId }
            AddressUtils.searchAddressesProblem({ temp, ui, api })
        }
        else {
            ui.ProblemMsg = "No completion details found for this job"
            ui.ProblemMsgVariant = "error"
            ui.ProblemShowMsg = true
        }
    }
}
export const importAddressAndContact = async (jobId: number) => {
    logger.debug(name + "->" + importAddressAndContact.name, `Import will be executed of JobId: ${jobId}`)
    if (ui.ActiveStep !== 0) {
        ui.ProblemMsg = "Address for property can be only changed on first page!"
        ui.ProblemMsgVariant = "warning"
        ui.ProblemShowMsg = true
        return
    }
    const booking = await api.fetchJobByJobId(jobId)
    if (booking) {
        ui.JobSearchModal = false;
        ui.TimeFrame = {
            "value": 2160,
            "label": "3 Months"
        }
        ui.PropertyContactTitle = booking!.customerTitle.trim()
        ui.PropertyContactMobile = booking!.telephoneMobile.trim()
        ui.PropertyContactLandline = booking!.telephoneLandline.trim()
        ui.PropertyContactTitle = booking!.customerTitle.trim()
        ui.PropertyContactFirstName = booking!.customerForename.trim()
        ui.PropertyContactLastName = booking!.customerSurname.trim()
        ui.PropertyAddressBusinessName = booking!.organisation.trim()
        ui.PropertyAddressIsBusiness = booking!.organisation ? true : false
        ui.PropertyContactEmail = booking!.emailAddress.trim()
        ui.ProblemInputPostcode = booking!.postcode.trim()
        ui.AddressToMatch = `${booking!.property} ${booking!.street}${booking!.locality ? (" " + booking!.locality + ",") : ""} ${booking!.town}, ${booking!.county ? (" " + booking!.county + ",") : ""} ${booking!.postcode}`
        ui.PropertyAddressCity = booking!.town.trim()
        ui.PropertyAddressLocality = booking!.locality.trim()
        ui.ProblemMsg = "Imported address and contact details"
        ui.ProblemMsgVariant = "success"
        ui.ProblemShowMsg = true
        ui.CallerName = booking!.customerTitle.trim() + ". " + booking!.customerForename.trim() + " " + booking!.customerSurname.trim()
        AddressUtils.searchAddressesProblem({ temp, ui, api })
    }
}

type Props = {
    data: Data[];
}

const JobsTable: React.FC<Props> = ({ data }) => {
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('jobId');
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    React.useEffect(() => {
        // clear selected on new search
        setSelected([]);
    }, [data]);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: readonly string[] = [];

        // if name in selected, remove it
        if (selectedIndex === -1) {
            newSelected = [name]
        } else {
            newSelected = []
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar selected={selected} numLength={data.length} validJobType={data.find(j => j.jobId === Number(selected))?.accountType.toUpperCase().startsWith("CARE") ? false : true} />
                <TableContainer>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size={'small'}
                        stickyHeader
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={data.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(data, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const isItemSelected = isSelected(String(row.jobId));
                                    const labelId = `enhanced - table - checkbox - ${index} `;

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, String(row.jobId))}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.jobId}
                                            selected={isItemSelected}
                                        >
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="normal"
                                                align="right"
                                            >
                                                {row.jobId}
                                            </TableCell>
                                            <TableCell align="right">{row.status}</TableCell>
                                            <TableCell align="right">{row.jobDescription}</TableCell>
                                            <TableCell align="right"><AccountTypeIcon tooltip={row.accountType} accountName={row.accountType} parent="" /></TableCell>
                                            <TableCell align="right">{row.customer}</TableCell>
                                            <TableCell align="right">{row.address}</TableCell>
                                            <TableCell align="right">{row.postcode}</TableCell>
                                            <TableCell align="right">{row.dateRequired}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (33) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ '.MuiTablePagination-selectLabel': { m: "unset" }, '.MuiTablePagination-displayedRows': { m: "unset" } }}
                    rowsPerPageOptions={[10, 15]}
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
}

JobsTable.displayName = name;

export default React.memo(JobsTable);